/**
 * SoundManager - Gerenciador de sons do jogo
 * Singleton que controla todos os efeitos sonoros com suporte a mute e persistência
 * 
 * @author Memória dos Imortais Team
 * @version 1.1.0
 */
class SoundManager {
  constructor() {
    // Implementar Singleton pattern
    if (SoundManager.instance) {
      return SoundManager.instance;
    }
    
    this.sounds = {};
    this.volume = 0.7; // Volume padrão 70%
    this.muted = false;
    
    this._loadPreferences();
    this._initializeSounds();
    
    SoundManager.instance = this;
  }

  /**
   * Inicializa todos os sons do jogo
   * @private
   */
  _initializeSounds() {
    // Resolver a URL base (subpasta no deploy do Firebase / GCP)
    const PUBLIC_URL = (process.env.PUBLIC_URL || (typeof window !== 'undefined' && window.location.pathname.includes('/memoria-dos-imortais') ? '/memoria-dos-imortais' : '')).replace(/\/$/, '');
    
    // Mapear fontes com suporte primário a .mp3 / .ogg e fallbacks de extensão
    this.soundDefinitions = {
      opening: [
        `${PUBLIC_URL}/sounds/opening.mp3`,
        `${PUBLIC_URL}/sounds/opening.MP3`,
        `${PUBLIC_URL}/sounds/opening.ogg`
      ],
      back: [
        `${PUBLIC_URL}/sounds/back.mp3`,
        `${PUBLIC_URL}/sounds/back.MP3`,
        `${PUBLIC_URL}/sounds/back.ogg`
      ],
      flip: [
        `${PUBLIC_URL}/sounds/flip.MP3`,
        `${PUBLIC_URL}/sounds/flip.mp3`
      ],
      match: [
        `${PUBLIC_URL}/sounds/match.MP3`,
        `${PUBLIC_URL}/sounds/match.mp3`
      ],
      combo: [
        `${PUBLIC_URL}/sounds/combo.MP3`,
        `${PUBLIC_URL}/sounds/combo.mp3`
      ],
      victory: [
        `${PUBLIC_URL}/sounds/victory.MP3`,
        `${PUBLIC_URL}/sounds/victory.mp3`
      ],
      defeat: [
        `${PUBLIC_URL}/sounds/defeat.MP3`,
        `${PUBLIC_URL}/sounds/defeat.mp3`
      ],
      'power-thoth': [
        `${PUBLIC_URL}/sounds/power-thoth.MP3`,
        `${PUBLIC_URL}/sounds/power-thoth.mp3`
      ],
      'power-anansi': [
        `${PUBLIC_URL}/sounds/power-anansi.MP3`,
        `${PUBLIC_URL}/sounds/power-anansi.mp3`
      ],
      'power-grio': [
        `${PUBLIC_URL}/sounds/power-grio.MP3`,
        `${PUBLIC_URL}/sounds/power-grio.mp3`
      ]
    };
    
    // Criar objetos Audio para cada som com tratamento de fallback
    Object.keys(this.soundDefinitions).forEach(key => {
      const urls = Array.isArray(this.soundDefinitions[key])
        ? this.soundDefinitions[key]
        : [this.soundDefinitions[key]];

      try {
        const audio = new Audio(urls[0]);
        audio.volume = this.volume;
        audio.preload = 'auto';

        // Tentar próximo fallback se houver falha de carregamento
        audio.onerror = () => {
          if (urls.length > 1 && audio.src !== urls[1]) {
            audio.src = urls[1];
          }
        };

        this.sounds[key] = audio;
      } catch (error) {
        console.warn(`Erro ao inicializar som ${key}:`, error);
      }
    });
  }

  /**
   * Carrega preferências salvas do localStorage
   * @private
   */
  _loadPreferences() {
    try {
      const savedMuted = localStorage.getItem('memoria_imortais_muted');
      const savedVolume = localStorage.getItem('memoria_imortais_volume');
      
      if (savedMuted !== null) {
        this.muted = JSON.parse(savedMuted);
      }
      
      if (savedVolume !== null) {
        this.volume = parseFloat(savedVolume);
        // Garantir que está no range válido
        this.volume = Math.max(0, Math.min(1, this.volume));
      }
    } catch (error) {
      console.warn('Erro ao carregar preferências de som:', error);
      // Usar valores padrão em caso de erro
      this.muted = false;
      this.volume = 0.7;
    }
  }

  /**
   * Salva preferências no localStorage
   * @private
   */
  _savePreferences() {
    try {
      localStorage.setItem('memoria_imortais_muted', JSON.stringify(this.muted));
      localStorage.setItem('memoria_imortais_volume', this.volume.toString());
    } catch (error) {
      console.warn('Erro ao salvar preferências de som:', error);
    }
  }

  /**
   * Toca um som específico
   * @param {string} soundName - Nome do som a ser tocado
   * @public
   */
  play(soundName) {
    // Não tocar se estiver mutado
    if (this.muted) return;
    
    const sound = this.sounds[soundName];
    if (!sound) {
      console.warn(`Som não encontrado: ${soundName}`);
      return;
    }
    
    try {
      // Resetar para o início e tocar
      sound.currentTime = 0;
      sound.play().catch(err => {
        // Ignorar erros de autoplay (comum em mobile)
        if (err.name !== 'NotAllowedError') {
          console.warn(`Erro ao tocar som ${soundName}:`, err);
        }
      });
    } catch (error) {
      console.warn(`Erro ao tocar som ${soundName}:`, error);
    }
  }

  /**
   * Define o volume geral
   * @param {number} level - Nível de volume (0.0 a 1.0)
   * @public
   */
  setVolume(level) {
    this.volume = Math.max(0, Math.min(1, level));
    
    // Atualizar volume de todos os sons
    Object.values(this.sounds).forEach(sound => {
      if (sound) {
        sound.volume = this.volume;
      }
    });
    
    this._savePreferences();
  }

  /**
   * Alterna entre mute/unmute
   * @returns {boolean} Novo estado de mute
   * @public
   */
  toggleMute() {
    this.muted = !this.muted;
    this._savePreferences();
    return this.muted;
  }

  /**
   * Retorna estado atual de mute
   * @returns {boolean} True se mutado
   * @public
   */
  isMuted() {
    return this.muted;
  }

  /**
   * Retorna volume atual
   * @returns {number} Volume (0.0 a 1.0)
   * @public
   */
  getVolume() {
    return this.volume;
  }

  /**
   * Pré-carrega todos os sons (opcional)
   * Útil para garantir que sons estejam prontos na primeira interação
   * @public
   */
  preloadAll() {
    Object.values(this.sounds).forEach(sound => {
      if (sound) {
        sound.load();
      }
    });
  }

  /**
   * Para todos os sons que estão tocando
   * @public
   */
  stopAll() {
    Object.values(this.sounds).forEach(sound => {
      if (sound) {
        sound.pause();
        sound.currentTime = 0;
      }
    });
  }
}

// Exportar instância singleton
const soundManager = new SoundManager();
export default soundManager;
